/**
 * TradeChart — Lightweight Charts integration for the trade card.
 *
 * Renders candlestick + volume bars with entry/exit markers and SL/TP price
 * lines. Supports timeframe switching, replay (bar-by-bar advance), and
 * dark/light mode.
 *
 * Props:
 *   bars            Array<{time:number, open, high, low, close, volume?}>
 *                   `time` is Unix seconds (preferred) or YYYY-MM-DD string.
 *   entries         Array<{time, price, quantity}>  — buy fills (for long)
 *                   or sell fills (for short)
 *   exits           Array<{time, price, quantity}>  — opposite-side fills
 *   direction       'long' | 'short' — controls marker color/position
 *   stopLoss        number? — draws a dashed red line
 *   takeProfit      number? — draws a dashed green line
 *   symbol          string — shown in the header
 *   timeframe       string — controlled timeframe ('1m' | '5m' | '15m' | '1h')
 *   onTimeframeChange  (tf) => void — required when timeframe is controlled
 *   enableReplay    bool (default true)
 *   darkMode        bool (default true)
 *   height          number (default 380)
 *
 * Phase A note: this component renders bars supplied via props. The data
 * source layer (Phase B) will fetch bars from a configured source — here
 * we just show whatever the parent gives us. For dev testing without real
 * data, see `src/utils/sampleBars.js`.
 */

import { useEffect, useRef, useState, useMemo } from 'react';
import {
  createChart,
  createSeriesMarkers,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  ColorType,
  LineStyle
} from 'lightweight-charts';
import { Play, Pause, RotateCcw, FastForward, Maximize2, RefreshCw } from 'lucide-react';
import { computeEmas, DEFAULT_EMA_PERIODS, DEFAULT_EMA_COLORS } from '../../services/barAnalysis/emas';
import { computeVWAP } from '../../services/barAnalysis/vwap';
import { getExecutionInstantMs, getEtParts } from '../../services/timeService';
import GlassSelect from '../Common/GlassSelect';

const VWAP_COLOR = '#a855f7'; // purple — distinct from all four default EMA colors

const TIMEFRAMES = ['1m', '5m', '15m', '1h', '4h'];
const REPLAY_SPEEDS = [
  { label: '1×', value: 1 },
  { label: '2×', value: 2 },
  { label: '4×', value: 4 },
  { label: '10×', value: 10 },
  { label: 'Max', value: 50 },
];

// Normalize a "time" value into Lightweight Charts' expected number (Unix seconds).
// Accepts: number (epoch sec or ms), Date, ISO string, "HH:MM" combined with dateStr.
//
// IMPORTANT: HH:MM strings in this codebase are always **Eastern Time** (per
// the storage contract in CLAUDE.md). The naive `new Date("2026-05-14T21:46:00")`
// approach broke this two ways:
//   1. It parses as **browser local time** — wrong for any non-ET user.
//   2. It anchors to the literal calendar date — wrong for evening fills.
//      A 21:46 fill on the "2026-05-14 trading day" actually happened at
//      21:46 ET on **May 13** (the futures session opens at 18:00 ET the
//      prior calendar day). The naive parse put markers on May 14 evening
//      instead, where there are no bars.
// `getExecutionInstantMs(tradingDate, "HH:MM")` does both correctly: ET-anchored
// + futures-session boundary aware. Same helper used by all the Coach analyzers.
const toUnixSec = (t, dateStr) => {
  if (t == null) return null;
  if (typeof t === 'number') return t > 1e12 ? Math.floor(t / 1000) : Math.floor(t);
  if (t instanceof Date) return Math.floor(t.getTime() / 1000);
  if (typeof t === 'string') {
    // "HH:MM" or "HH:MM:SS" — needs dateStr (the trading day) to anchor as ET.
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(t) && dateStr) {
      // getExecutionInstantMs only consumes HH:MM; strip seconds if present.
      const hhmm = t.length >= 5 ? t.slice(0, 5) : t;
      const ms = getExecutionInstantMs(dateStr, hhmm);
      return Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : null;
    }
    const ms = new Date(t).getTime();
    return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }
  return null;
};

// Format a Unix-seconds timestamp as **ET** HH:MM (24-hour). The chart is a
// futures journal anchored to ET sessions — the trader's mental model is the
// ET clock (open 18:00, close 17:00 ET) regardless of where they're physically
// running the app. Browser-local formatting would drift labels away from the
// stored entry/exit times for any user not in ET.
//
// Defensive: Lightweight Charts v5's `Time` type is a union of UTCTimestamp |
// BusinessDay | string. We normalize all three so the formatter never returns
// null/empty (which would HIDE the tick mark entirely — a sneaky way for
// labels to disappear).
const _normalizeTimeToMs = (time) => {
  if (typeof time === 'number') return time * 1000;
  if (time && typeof time === 'object' && 'year' in time) {
    // BusinessDay { year, month, day }
    return new Date(time.year, (time.month || 1) - 1, time.day || 1).getTime();
  }
  if (typeof time === 'string') {
    const ms = Date.parse(time);
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
};
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const formatLocalHM = (time) => {
  const ms = _normalizeTimeToMs(time);
  if (ms == null) return '';
  try {
    const parts = getEtParts(new Date(ms));
    if (!parts) return '';
    const h = String(parts.hour).padStart(2, '0');
    const m = String(parts.minute).padStart(2, '0');
    return `${h}:${m}`;
  } catch {
    return '';
  }
};
const formatLocalShortDate = (time) => {
  const ms = _normalizeTimeToMs(time);
  if (ms == null) return '';
  try {
    const parts = getEtParts(new Date(ms));
    if (!parts) return '';
    // parts.month/day are 2-digit zero-padded strings ("05", "03"); strip the
    // leading zero on day for human-readable "May 3" instead of "May 03".
    const dayNum = parseInt(parts.day, 10);
    const monthIdx = (parseInt(parts.month, 10) - 1) % 12;
    return `${MONTH_SHORT[monthIdx]} ${dayNum}`;
  } catch {
    return '';
  }
};

const TradeChart = ({
  bars = [],
  entries = [],
  exits = [],
  direction = 'long',
  stopLoss,
  takeProfit,
  symbol = '',
  timeframe = '5m',
  onTimeframeChange,
  // Optional manual-refresh callback. When supplied, a Refresh button
  // appears in the toolbar. Parent should bypass the bar cache so the
  // user gets a true "force pull from source" — not a cache hit.
  onRefresh,
  // True while a refresh fetch is in flight; spins the icon.
  isRefreshing = false,
  tradeDate,                  // YYYY-MM-DD — used to anchor "HH:MM" times in entries/exits
  enableReplay = true,
  darkMode = true,
  height = 380,
  // Bar-derived MAE/MFE result, when available. When non-null we render
  // green (MFE peak) and red (MAE trough) circle markers at the bars
  // where each occurred, so the Coach's text observations are anchored
  // to a visible point on the chart.
  barAnalysis = null,
  // EMA overlay periods (e.g. [9, 21, 50, 200]). Default = the standard set
  // from emas.js. Pass an empty array to disable. Each period gets a line
  // series; colors come from DEFAULT_EMA_COLORS unless overridden via
  // `emaColors` prop.
  emaPeriods = DEFAULT_EMA_PERIODS,
  emaColors = DEFAULT_EMA_COLORS,
  // VWAP overlay. Session-anchored, computed from bars on the fly. Defaults
  // to enabled — futures intraday traders almost universally use it.
  showVwap = true,
  // Initial zoom: how many bars to show centered on the trade entry on
  // first render. Default 50 — close enough to read candles cleanly while
  // keeping pre/post-trade context visible. The "Fit to content" button
  // still expands to all loaded bars on demand.
  defaultZoomBars = 72,  // 72 = ~6 hours of 5m bars (3h pre + 3h post entry)
}) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const markersPluginRef = useRef(null); // v5 markers live on a plugin, not the series
  const slLineRef = useRef(null);
  const tpLineRef = useRef(null);
  const replayTimerRef = useRef(null);
  // Map of period (number) → LineSeries. Created lazily on first render of
  // each period; removed when a period is dropped from `emaPeriods`. Cleared
  // wholesale on chart teardown so we don't leak series across remounts.
  const emaSeriesRefs = useRef({});
  const vwapSeriesRef = useRef(null);

  // VWAP visibility — owned by the chart so toggling doesn't re-render the
  // parent. Initialized from the `showVwap` prop.
  const [vwapVisible, setVwapVisible] = useState(showVwap);
  useEffect(() => { setVwapVisible(showVwap); }, [showVwap]);

  // Per-period visibility toggle. Defaults to all-on; user can hide individual
  // EMAs via the toggle row. State is owned by the chart so toggling doesn't
  // force a re-render of the parent modal.
  const [emaVisible, setEmaVisible] = useState(() =>
    Object.fromEntries((emaPeriods || []).map(p => [p, true]))
  );
  // Sync visibility map when caller changes the period set (e.g. user
  // edits Settings later — not yet wired but ready for it).
  useEffect(() => {
    setEmaVisible(prev => {
      const next = {};
      for (const p of emaPeriods || []) {
        next[p] = prev[p] !== undefined ? prev[p] : true;
      }
      return next;
    });
  }, [emaPeriods]);

  // Replay state — `idx` is how many bars are currently visible (0..bars.length).
  // When playing, we advance idx on a ticker. When paused at the end (or never
  // started), we show all bars.
  const [replayIdx, setReplayIdx] = useState(bars.length);
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState(4);

  // Re-anchor replay to "show all" whenever bars array changes (timeframe swap,
  // new data, etc.) — otherwise stale idx points past the new array.
  useEffect(() => {
    setReplayIdx(bars.length);
    setReplayPlaying(false);
  }, [bars]);

  // ── Build chart on mount + theme change ─────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: darkMode ? '#0b0e14' : '#ffffff' },
        textColor: darkMode ? '#d1d5db' : '#374151',
      },
      grid: {
        vertLines: { color: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' },
        horzLines: { color: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' },
      },
      timeScale: {
        // Belt-and-suspenders: explicitly enable visibility + border. Defaults
        // are `true` per the v5 docs but we've burned a day on missing time
        // labels — making intent explicit removes "is the default what I think
        // it is?" as a possibility during future debugging.
        visible: true,
        borderVisible: true,
        timeVisible: true,
        secondsVisible: false,
        fontSize: 12,
        borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
        // Local-timezone formatter for axis labels — Lightweight Charts
        // defaults to UTC, which would make a 10:18 AM trade appear at
        // "14:00" for an ET user. Three-layer fallback guarantees a
        // non-empty return; v5 hides the tick mark entirely on empty
        // string.
        tickMarkFormatter: (time, tickMarkType) => {
          const primary = (tickMarkType >= 3) ? formatLocalHM(time) : formatLocalShortDate(time);
          if (primary) return primary;
          try {
            if (typeof time === 'number') return new Date(time * 1000).toLocaleTimeString();
            return String(time) || '—';
          } catch {
            return '—';
          }
        },
      },
      // Crosshair tooltip date/time — ET-anchored, same reasoning as the
      // axis formatters above.
      localization: {
        timeFormatter: (time) => {
          const ms = typeof time === 'number' ? time * 1000 : _normalizeTimeToMs(time);
          if (ms == null) return '';
          try {
            const parts = getEtParts(new Date(ms));
            if (!parts) return '';
            const dayNum = parseInt(parts.day, 10);
            const monthIdx = (parseInt(parts.month, 10) - 1) % 12;
            return `${MONTH_SHORT[monthIdx]} ${dayNum}, ${parts.year} ${formatLocalHM(time)}`;
          } catch {
            return '';
          }
        },
      },
      rightPriceScale: {
        borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
      },
      crosshair: {
        mode: 1, // CrosshairMode.Normal
      },
      width: containerRef.current.clientWidth,
      height,
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#10b981',
      downColor: '#ef4444',
      borderUpColor: '#10b981',
      borderDownColor: '#ef4444',
      wickUpColor: '#10b981',
      wickDownColor: '#ef4444',
    });

    // Volume series — pinned to its own pane (index 1). Putting volume in
    // a separate pane is v5's recommended pattern: the time axis sits at
    // the bottom of the chart frame and is shared across panes, so volume's
    // scale margins can't accidentally overlap or push the time axis off-
    // canvas. We previously used an overlay (priceScaleId: 'volume' with
    // scaleMargins.top: 0.85) and that combo had a long-standing bug where
    // the time axis labels intermittently disappeared after a re-render —
    // hard to reproduce, easy to permanently fix by separating the panes.
    const volumeSeries = chart.addSeries(
      HistogramSeries,
      {
        priceFormat: { type: 'volume' },
        priceScaleId: '',  // pane-local overlay; conflict-free since it's alone in pane 1
      },
      1,  // pane index — 0 is the candle pane, 1 is the new volume pane
    );
    // Size the volume pane to ~20% of the chart's height. v5's pane API:
    // chart.panes() returns an array; setHeight is called on the second pane.
    // Wrap in try/catch in case the pane wasn't created yet (defensive).
    try {
      const panes = chart.panes();
      if (panes && panes.length >= 2 && typeof panes[1].setHeight === 'function') {
        panes[1].setHeight(Math.max(60, Math.round(height * 0.18)));
      }
    } catch (e) {
      // Older lightweight-charts builds may not expose chart.panes() — the
      // overlay still renders, just without explicit sizing.
      // eslint-disable-next-line no-console
      console.warn('[TradeChart] Pane sizing not available:', e?.message || e);
    }

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;
    volumeSeriesRef.current = volumeSeries;
    // v5: markers are a series-attached plugin, not a method on the series itself
    markersPluginRef.current = createSeriesMarkers(candleSeries, []);

    // Auto-resize on container width change
    const ro = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
      }
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      volumeSeriesRef.current = null;
      markersPluginRef.current = null;
      slLineRef.current = null;
      tpLineRef.current = null;
      // EMA + VWAP series are owned by the chart, so chart.remove() above
      // already disposed them. Just clear our refs so re-mount starts fresh.
      emaSeriesRefs.current = {};
      vwapSeriesRef.current = null;
    };
  }, [darkMode, height]);

  // ── EMA overlays ──────────────────────────────────────────────────────
  // Compute EMA series from current bars + render one Lightweight Charts
  // LineSeries per requested period. Toggling `emaVisible[period]` flips
  // visibility without re-creating the series (cheaper than add/remove).
  const emaData = useMemo(() => {
    if (!Array.isArray(bars) || bars.length === 0) return {};
    if (!Array.isArray(emaPeriods) || emaPeriods.length === 0) return {};
    return computeEmas(bars, emaPeriods);
  }, [bars, emaPeriods]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const wantedPeriods = new Set((emaPeriods || []).map(p => Number(p)));
    const existing = emaSeriesRefs.current;

    // Remove series for periods that are no longer requested
    for (const periodKey of Object.keys(existing)) {
      const p = Number(periodKey);
      if (!wantedPeriods.has(p)) {
        try { chart.removeSeries(existing[periodKey]); } catch { /* gone */ }
        delete existing[periodKey];
      }
    }

    // Create / update series for each requested period
    for (const p of wantedPeriods) {
      const data = emaData[p] || [];
      // Only feed valid points to the chart — null gaps would produce NaN
      // markers in the line. We drop them (the chart joins across gaps,
      // which is the correct behavior for warm-up periods).
      const lineData = data
        .filter(pt => pt && pt.value !== null && Number.isFinite(pt.value) && Number.isFinite(pt.time))
        .map(pt => ({ time: pt.time, value: pt.value }));

      let s = existing[p];
      if (!s) {
        const color = emaColors?.[p] || '#9ca3af';
        s = chart.addSeries(LineSeries, {
          color,
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
          title: `EMA${p}`,
        });
        existing[p] = s;
      }
      s.setData(lineData);
      s.applyOptions({ visible: emaVisible[p] !== false });
    }
  }, [emaData, emaPeriods, emaColors, emaVisible]);

  // ── VWAP overlay ──────────────────────────────────────────────────────
  // Single line series, session-anchored. Memoize the data so toggle clicks
  // don't recompute the VWAP curve.
  const vwapData = useMemo(() => {
    if (!Array.isArray(bars) || bars.length === 0) return [];
    return computeVWAP(bars);
  }, [bars]);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    // Lazy-create the series on first render
    if (!vwapSeriesRef.current) {
      vwapSeriesRef.current = chart.addSeries(LineSeries, {
        color: VWAP_COLOR,
        lineWidth: 2,                    // slightly thicker than EMAs to stand out
        lineStyle: LineStyle.Solid,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
        title: 'VWAP',
      });
    }
    const lineData = (vwapData || [])
      .filter(pt => pt && pt.value !== null && Number.isFinite(pt.value) && Number.isFinite(pt.time))
      .map(pt => ({ time: pt.time, value: pt.value }));
    vwapSeriesRef.current.setData(lineData);
    vwapSeriesRef.current.applyOptions({ visible: vwapVisible });
  }, [vwapData, vwapVisible]);

  // ── Push bars + volume into the series ──────────────────────────────────
  // When replaying, we slice up to replayIdx so bars appear progressively.
  useEffect(() => {
    if (!candleSeriesRef.current || !volumeSeriesRef.current) return;

    const visible = replayPlaying || replayIdx < bars.length
      ? bars.slice(0, replayIdx)
      : bars;

    const candleData = visible
      .map(b => {
        const t = toUnixSec(b.time, tradeDate);
        if (t == null) return null;
        return {
          time: t,
          open: Number(b.open),
          high: Number(b.high),
          low: Number(b.low),
          close: Number(b.close),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.time - b.time);

    const volumeData = visible
      .map(b => {
        const t = toUnixSec(b.time, tradeDate);
        if (t == null) return null;
        const isUp = Number(b.close) >= Number(b.open);
        return {
          time: t,
          value: Number(b.volume) || 0,
          color: isUp
            ? (darkMode ? 'rgba(16,185,129,0.5)' : 'rgba(16,185,129,0.6)')
            : (darkMode ? 'rgba(239,68,68,0.5)' : 'rgba(239,68,68,0.6)'),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.time - b.time);

    candleSeriesRef.current.setData(candleData);
    volumeSeriesRef.current.setData(volumeData);

    // Initial zoom: show `defaultZoomBars` bars (default 72 = ~6 hours of
    // 5m candles, 3h pre + 3h post entry) centered on the trade entry,
    // OR all bars if we have fewer than that loaded. The full session's
    // bars remain in the data — the user can scroll/zoom out (or hit the
    // "Fit to content" button) to see anything outside this window.
    //
    // Same behavior for past trades and today's trade. Earlier iterations
    // had a separate past-trade path that tried to extend the visible
    // range to session close (5pm ET); that's redundant when the user
    // can just scroll to see the rest of the session, and the time-based
    // setVisibleRange variant we tried to enable that hid the time-axis
    // labels in lightweight-charts v5.x.
    if (!replayPlaying && replayIdx >= bars.length && candleData.length > 0) {
      const ts = chartRef.current?.timeScale();
      if (ts) {
        const target = Math.max(5, Math.floor(defaultZoomBars) || 72);

        // Find the bar closest to the trade's first entry time, if known.
        let entryIdx = -1;
        if (Array.isArray(entries) && entries.length > 0) {
          const entryTs = toUnixSec(entries[0]?.time, tradeDate);
          if (entryTs != null) {
            let bestDiff = Infinity;
            for (let i = 0; i < candleData.length; i++) {
              const diff = Math.abs(candleData[i].time - entryTs);
              if (diff < bestDiff) { bestDiff = diff; entryIdx = i; }
            }
          }
        }

        if (candleData.length <= target) {
          // Less than a window of bars loaded — just fit them all.
          ts.fitContent();
        } else {
          // Center the window on entry; fall back to the right edge if
          // entry isn't resolvable.
          let centerIdx = entryIdx >= 0 ? entryIdx : candleData.length - 1;
          const half = Math.floor(target / 2);
          let from = Math.max(0, centerIdx - half);
          let to   = Math.min(candleData.length - 1, centerIdx + half);
          // If we hit either edge, expand the other side to keep the window
          // size at the requested count.
          if (to - from + 1 < target) {
            if (from === 0) to = Math.min(candleData.length - 1, target - 1);
            else if (to === candleData.length - 1) from = Math.max(0, to - target + 1);
          }
          try { ts.setVisibleLogicalRange({ from, to }); }
          catch { ts.fitContent(); }
        }
      }
    }
  }, [bars, replayIdx, replayPlaying, tradeDate, darkMode, defaultZoomBars, entries]);

  // ── Markers (entries + exits) ───────────────────────────────────────────
  // Hide markers whose bar hasn't arrived yet during replay.
  useEffect(() => {
    if (!candleSeriesRef.current) return;

    const cutoffTime = (replayPlaying || replayIdx < bars.length) && bars[replayIdx - 1]
      ? toUnixSec(bars[replayIdx - 1].time, tradeDate)
      : Infinity;

    // Long entry = green up arrow below bar; long exit = red down arrow above bar.
    // Short flips: short entry = red down arrow above; short exit = green up arrow below.
    const isLong = direction !== 'short';
    const entryColor = isLong ? '#10b981' : '#ef4444';
    const exitColor = isLong ? '#ef4444' : '#10b981';
    const entryShape = isLong ? 'arrowUp' : 'arrowDown';
    const exitShape = isLong ? 'arrowDown' : 'arrowUp';
    const entryPos = isLong ? 'belowBar' : 'aboveBar';
    const exitPos = isLong ? 'aboveBar' : 'belowBar';

    const markers = [];

    (entries || []).forEach((e, i) => {
      const t = toUnixSec(e.time, tradeDate);
      if (t == null || t > cutoffTime) return;
      const qty = e.quantity || e.qty || 1;
      const price = Number(e.price);
      markers.push({
        time: t,
        position: entryPos,
        color: entryColor,
        shape: entryShape,
        text: `${isLong ? 'BUY' : 'SELL'} ${qty}@${price}`,
        size: entries.length > 1 ? 1 : 1.5,
        id: `entry-${i}`,
      });
    });

    (exits || []).forEach((e, i) => {
      const t = toUnixSec(e.time, tradeDate);
      if (t == null || t > cutoffTime) return;
      const qty = e.quantity || e.qty || 1;
      const price = Number(e.price);
      markers.push({
        time: t,
        position: exitPos,
        color: exitColor,
        shape: exitShape,
        text: `${isLong ? 'SELL' : 'COVER'} ${qty}@${price}`,
        size: exits.length > 1 ? 1 : 1.5,
        id: `exit-${i}`,
      });
    });

    // ── MAE / MFE markers (when barAnalysis available) ──────────────────
    // Distinctive circle shape so they read as "annotation" not "fill".
    // Position rules mirror the entry/exit logic:
    //   Long:  MFE = bar's HIGH side → aboveBar (green)
    //          MAE = bar's LOW side  → belowBar (red)
    //   Short: flipped — MFE at the low (favorable for short), MAE at the high.
    if (barAnalysis && Number.isFinite(barAnalysis.mfeAt) && Number.isFinite(barAnalysis.maeAt)) {
      // Only render markers for bars that have arrived during replay
      if (Number.isFinite(barAnalysis.mfeAt) && barAnalysis.mfeAt <= cutoffTime && barAnalysis.mfePoints > 0) {
        const rPart = Number.isFinite(barAnalysis.mfeR) ? ` (${barAnalysis.mfeR >= 0 ? '+' : ''}${barAnalysis.mfeR.toFixed(2)}R)` : '';
        const dollarPart = Number.isFinite(barAnalysis.mfeDollars) ? ` +$${Math.round(barAnalysis.mfeDollars).toLocaleString()}` : ` ${barAnalysis.mfePoints.toFixed(2)} pts`;
        markers.push({
          time: barAnalysis.mfeAt,
          position: isLong ? 'aboveBar' : 'belowBar',
          color: '#22c55e',                 // brighter green than entry to differentiate
          shape: 'circle',
          text: `MFE${dollarPart}${rPart}`,
          size: 1.2,
          id: 'mfe-peak',
        });
      }
      if (Number.isFinite(barAnalysis.maeAt) && barAnalysis.maeAt <= cutoffTime && barAnalysis.maePoints > 0) {
        const rPart = Number.isFinite(barAnalysis.maeR) ? ` (-${barAnalysis.maeR.toFixed(2)}R)` : '';
        const dollarPart = Number.isFinite(barAnalysis.maeDollars) ? ` -$${Math.round(barAnalysis.maeDollars).toLocaleString()}` : ` ${barAnalysis.maePoints.toFixed(2)} pts`;
        markers.push({
          time: barAnalysis.maeAt,
          position: isLong ? 'belowBar' : 'aboveBar',
          color: '#dc2626',                 // brighter red than exit to differentiate
          shape: 'circle',
          text: `MAE${dollarPart}${rPart}`,
          size: 1.2,
          id: 'mae-trough',
        });
      }
    }

    markers.sort((a, b) => a.time - b.time);
    // v5: setMarkers lives on the markers plugin, not the series
    if (markersPluginRef.current && typeof markersPluginRef.current.setMarkers === 'function') {
      markersPluginRef.current.setMarkers(markers);
    }
  }, [entries, exits, direction, replayIdx, replayPlaying, bars, tradeDate, barAnalysis]);

  // ── SL / TP price lines ─────────────────────────────────────────────────
  useEffect(() => {
    if (!candleSeriesRef.current) return;

    if (slLineRef.current) {
      try { candleSeriesRef.current.removePriceLine(slLineRef.current); } catch { /* gone */ }
      slLineRef.current = null;
    }
    if (tpLineRef.current) {
      try { candleSeriesRef.current.removePriceLine(tpLineRef.current); } catch { /* gone */ }
      tpLineRef.current = null;
    }

    if (typeof stopLoss === 'number' && Number.isFinite(stopLoss)) {
      slLineRef.current = candleSeriesRef.current.createPriceLine({
        price: stopLoss,
        color: '#ef4444',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: 'SL',
      });
    }
    if (typeof takeProfit === 'number' && Number.isFinite(takeProfit)) {
      tpLineRef.current = candleSeriesRef.current.createPriceLine({
        price: takeProfit,
        color: '#10b981',
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        axisLabelVisible: true,
        title: 'TP',
      });
    }
  }, [stopLoss, takeProfit]);

  // ── Replay ticker ───────────────────────────────────────────────────────
  useEffect(() => {
    if (replayTimerRef.current) {
      clearInterval(replayTimerRef.current);
      replayTimerRef.current = null;
    }
    if (!replayPlaying) return;
    if (replayIdx >= bars.length) {
      setReplayPlaying(false);
      return;
    }
    // Speed: 1× = 500ms/bar, 2× = 250ms, 4× = 125ms, 10× = 50ms, Max = 10ms
    const intervalMs = Math.max(10, Math.floor(500 / replaySpeed));
    replayTimerRef.current = setInterval(() => {
      setReplayIdx(prev => {
        if (prev >= bars.length) {
          clearInterval(replayTimerRef.current);
          replayTimerRef.current = null;
          setReplayPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, intervalMs);
    return () => {
      if (replayTimerRef.current) clearInterval(replayTimerRef.current);
    };
  }, [replayPlaying, replaySpeed, bars.length, replayIdx]);

  // ── Replay controls ─────────────────────────────────────────────────────
  const handleReplayToggle = () => {
    if (replayIdx >= bars.length) {
      // Restart from beginning
      setReplayIdx(0);
      setReplayPlaying(true);
    } else {
      setReplayPlaying(p => !p);
    }
  };
  const handleReplayReset = () => {
    setReplayPlaying(false);
    setReplayIdx(0);
  };
  const handleReplayEnd = () => {
    setReplayPlaying(false);
    setReplayIdx(bars.length);
  };
  const handleScrubberChange = (e) => {
    const idx = parseInt(e.target.value, 10);
    setReplayPlaying(false);
    setReplayIdx(idx);
  };
  const handleFitContent = () => {
    chartRef.current?.timeScale().fitContent();
  };

  // ── Styling tokens ──────────────────────────────────────────────────────
  const btnBase = `text-[11px] font-medium px-2 py-1 rounded transition-colors`;
  const btnInactive = darkMode
    ? `bg-white/[0.03] text-gray-300 hover:bg-white/[0.08] border border-white/10`
    : `bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200`;
  const btnActive = darkMode
    ? `bg-purple-500/20 text-purple-100 border border-purple-400/40`
    : `bg-purple-100 text-purple-800 border border-purple-300`;

  const replayAtEnd = replayIdx >= bars.length;

  return (
    <div className={`rounded-lg border overflow-hidden shrink-0 ${darkMode ? 'bg-black/30 border-white/10' : 'bg-white border-gray-200'}`}>
      {/* Header / controls bar */}
      <div className={`flex items-center justify-between gap-2 flex-wrap px-3 py-2 border-b ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-purple-300/80' : 'text-purple-700/80'}`}>
            {symbol || 'Chart'}
          </span>
          {tradeDate && (
            <span className={`text-[11px] ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {tradeDate}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* EMA toggles — one chip per period. Click toggles visibility
              without recreating the chart series (cheap). Chip swatch
              reflects the line color so the user can connect chip to line.
              VWAP chip lives at the end of the same row for visual grouping
              (overlay indicators) — distinct color from all EMAs. */}
          {((Array.isArray(emaPeriods) && emaPeriods.length > 0) || true) && (
            <div className="flex gap-0.5 items-center">
              {(emaPeriods || []).map(p => {
                const visible = emaVisible[p] !== false;
                const color = emaColors?.[p] || '#9ca3af';
                return (
                  <button
                    key={`ema-${p}`}
                    type="button"
                    onClick={() => setEmaVisible(prev => ({ ...prev, [p]: !visible }))}
                    className={`${btnBase} flex items-center gap-1 ${
                      visible ? btnActive : btnInactive
                    } ${visible ? '' : 'opacity-60'}`}
                    title={`${visible ? 'Hide' : 'Show'} EMA${p}`}
                  >
                    <span
                      className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: color, opacity: visible ? 1 : 0.4 }}
                    />
                    <span>{p}</span>
                  </button>
                );
              })}
              <button
                key="vwap"
                type="button"
                onClick={() => setVwapVisible(v => !v)}
                className={`${btnBase} flex items-center gap-1 ${
                  vwapVisible ? btnActive : btnInactive
                } ${vwapVisible ? '' : 'opacity-60'}`}
                title={`${vwapVisible ? 'Hide' : 'Show'} VWAP`}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: VWAP_COLOR, opacity: vwapVisible ? 1 : 0.4 }}
                />
                <span>VWAP</span>
              </button>
            </div>
          )}

          {/* Timeframe selector */}
          <div className="flex gap-0.5">
            {TIMEFRAMES.map(tf => (
              <button
                key={tf}
                type="button"
                onClick={() => onTimeframeChange?.(tf)}
                className={`${btnBase} ${tf === timeframe ? btnActive : btnInactive}`}
                title={`Switch to ${tf}`}
              >
                {tf}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={handleFitContent}
            className={`${btnBase} ${btnInactive} flex items-center gap-1`}
            title="Fit to content"
          >
            <Maximize2 size={11} />
          </button>

          {/* Manual refresh — only renders when parent supplies onRefresh.
              Bypasses the bar cache (parent's responsibility) so this is
              a "force pull from source" button, not a cosmetic re-render. */}
          {typeof onRefresh === 'function' && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              className={`${btnBase} ${btnInactive} flex items-center gap-1 ${isRefreshing ? 'opacity-60 cursor-wait' : ''}`}
              title="Refresh chart (pull latest bars from source, bypass cache)"
            >
              <RefreshCw size={11} className={isRefreshing ? 'animate-spin' : ''} />
            </button>
          )}
        </div>
      </div>

      {/* Chart */}
      <div ref={containerRef} className="w-full" style={{ height }} />

      {/* Replay bar */}
      {enableReplay && bars.length > 1 && (
        <div className={`flex items-center gap-2 px-3 py-2 border-t ${darkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <button
            type="button"
            onClick={handleReplayReset}
            disabled={replayIdx === 0}
            className={`${btnBase} ${replayIdx === 0 ? 'opacity-40 cursor-not-allowed' : ''} ${btnInactive} flex items-center gap-1`}
            title="Reset to start"
          >
            <RotateCcw size={11} />
          </button>

          <button
            type="button"
            onClick={handleReplayToggle}
            className={`${btnBase} ${btnActive} flex items-center gap-1 min-w-[60px] justify-center`}
            title={replayPlaying ? 'Pause' : (replayAtEnd ? 'Restart' : 'Play')}
          >
            {replayPlaying ? <Pause size={11} /> : <Play size={11} />}
            {replayPlaying ? 'Pause' : (replayAtEnd ? 'Restart' : 'Play')}
          </button>

          <button
            type="button"
            onClick={handleReplayEnd}
            disabled={replayAtEnd}
            className={`${btnBase} ${replayAtEnd ? 'opacity-40 cursor-not-allowed' : ''} ${btnInactive} flex items-center gap-1`}
            title="Skip to end"
          >
            <FastForward size={11} />
          </button>

          {/* Scrubber */}
          <input
            type="range"
            min={0}
            max={bars.length}
            value={replayIdx}
            onChange={handleScrubberChange}
            className="flex-1 accent-purple-500 cursor-pointer"
            aria-label="Replay position"
          />

          <span className={`text-[11px] font-mono tabular-nums shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {replayIdx}/{bars.length}
          </span>

          {/* Speed selector */}
          <div className="min-w-[64px]">
            <GlassSelect
              value={replaySpeed}
              onChange={(v) => setReplaySpeed(parseInt(v, 10))}
              options={REPLAY_SPEEDS.map(s => ({ value: s.value, label: s.label }))}
              darkMode={darkMode}
              size="sm"
              ariaLabel="Replay speed"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeChart;
